'use client'

import * as React from 'react'

import { ChallengeIcon, ManageBookIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { DateRangeValue } from '../date-range-select'
import { DateRangeSelect } from '../date-range-select'
import { SchoolClassSelect } from '../school-class-select'

type ActivityView = 'lesson' | 'reading'

// Pill toggle styling mirrors the favorite page's tab pills (ui/shared/src/pages/favorite/index.tsx).
const activityPillClassName = cn(
  typographyVariants({ variant: 'label-md' }),
  'srd:inline-flex srd:h-auto srd:flex-none srd:items-center srd:gap-2 srd:rounded-xl srd:border srd:border-blue-70 srd:px-4 srd:py-2',
  'srd:text-grey-80 srd:transition-colors srd:hover:text-grey-100',
)

const activityPillActiveClassName = 'srd:bg-blue-30 srd:border-blue-30 srd:text-navy-80 srd:hover:text-navy-80'

interface TrendFilterHeaderProps {
  type: 'web' | 'school'
  /** Active Aktivitas sub-view. When provided with `onActivityViewChange`, the title + view pills render. */
  activityView?: ActivityView
  onActivityViewChange?: (view: ActivityView) => void
  /** School-class filter value (`type === 'school'` only). `null` = all classes. */
  schoolClassId?: string | null
  /** When provided alongside `type === 'school'`, the class select is shown. */
  onSchoolClassChange?: (value: string | null) => void
  /** Resolved date range. When `onDateRangeChange` is provided, the date-range select + range label show. */
  dateRange?: DateRangeValue
  onDateRangeChange?: (range: DateRangeValue) => void
}

/**
 * Shared header for the Aktivitas trend tables: the "Aktivitas" title + Membaca/Belajar view pills,
 * the class select, the date-range select, and the resolved range label.
 */
export function TrendFilterHeader({
  type,
  activityView,
  onActivityViewChange,
  schoolClassId,
  onSchoolClassChange,
  dateRange,
  onDateRangeChange,
}: TrendFilterHeaderProps) {
  const showViewToggle = activityView !== undefined && onActivityViewChange !== undefined
  const showDateSelect = onDateRangeChange !== undefined

  return (
    <div className='srd:flex srd:w-full srd:flex-col srd:gap-4 srd:max-w-[1200px] srd:mx-auto srd:px-4'>
      {showViewToggle && (
        <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-start srd:gap-4'>
          <h6 className={cn(typographyVariants({ variant: 'title-md' }))}>Aktivitas</h6>
          <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-start srd:gap-2'>
            <button
              aria-pressed={activityView === 'reading'}
              onClick={() => onActivityViewChange('reading')}
              className={cn(activityPillClassName, activityView === 'reading' && activityPillActiveClassName)}
            >
              <ManageBookIcon className='srd:size-5 srd:shrink-0' size={20} color='currentColor' />
              Membaca
            </button>
            <button
              aria-pressed={activityView === 'lesson'}
              onClick={() => onActivityViewChange('lesson')}
              className={cn(activityPillClassName, activityView === 'lesson' && activityPillActiveClassName)}
            >
              <ChallengeIcon className='srd:size-5 srd:shrink-0' size={20} color='currentColor' />
              Belajar
            </button>
          </div>
        </div>
      )}

      <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-4'>
        {type === 'school' && onSchoolClassChange && (
          <SchoolClassSelect
            value={schoolClassId ?? null}
            onValueChange={onSchoolClassChange}
            showNoClass
            className='srd:w-full srd:max-w-[330px] srd:data-[size=default]:h-10'
          />
        )}

        {onDateRangeChange && (
          <DateRangeSelect
            value={dateRange}
            onChange={onDateRangeChange}
            size='default'
            className='srd:data-[size=default]:h-10 srd:w-full srd:max-w-[330px]'
          />
        )}
      </div>

      {showDateSelect && dateRange && (
        <p className={cn(typographyVariants({ variant: 'body-md' }))}>
          {formatDate(dateRange.from, { dateStyle: 'long' })} - {formatDate(dateRange.to, { dateStyle: 'long' })}
        </p>
      )}
    </div>
  )
}
